import { useCallback, useMemo, useState } from 'react';

import { useLocalStorageValue } from 'src/app/shared/hooks/storage';

const STORAGE_KEY = 'starterDashboardV2_sharingTools_preferredFirstCard';

const DEFAULT_ORDER = ['recommended-groups', 'ready-to-share-posts', 'social-content', 'qr-poster'];

type UseSharingToolsOrderReturn = {
	orderedKeys: string[];
	initialIndex: number;
	registerClick: (clickedKey: string) => void;
};

export function useSharingToolsOrder(hasRecommendedGroups: boolean): UseSharingToolsOrderReturn {
	const [preferredFirstCard, setPreferredFirstCard] = useLocalStorageValue(STORAGE_KEY);
	const [initialPreferredFirstCard] = useState(preferredFirstCard);
	const orderedKeys = useMemo(
		() => (hasRecommendedGroups ? DEFAULT_ORDER : DEFAULT_ORDER.filter((key) => key !== 'recommended-groups')),
		[hasRecommendedGroups],
	);

	const initialIndex = useMemo(() => {
		// If user has a preferred card, use that
		if (initialPreferredFirstCard) {
			const preferredIndex = orderedKeys.indexOf(initialPreferredFirstCard);
			if (preferredIndex !== -1) {
				return preferredIndex;
			}
		}

		// Default: start at first card
		return 0;
	}, [initialPreferredFirstCard, orderedKeys]);

	const registerClick = useCallback(
		(clickedKey: string) => {
			// Validate the clicked key is in the order
			if (!orderedKeys.includes(clickedKey)) {
				return;
			}

			// Find the next card in the order (wrap around to first if at the end)
			const currentIndex = orderedKeys.indexOf(clickedKey);
			const nextIndex = (currentIndex + 1) % orderedKeys.length;
			const nextKey = orderedKeys[nextIndex];

			setPreferredFirstCard(nextKey);
		},
		[orderedKeys, setPreferredFirstCard],
	);

	return { orderedKeys, initialIndex, registerClick };
}
